import type { Word } from '../types';

/**
 * The Content Engine stores user-picked content as numeric suffix strings on
 * singleSelect fields (e.g., selected_wrong_1 = "3" means "the user picked
 * wrong_def_3 as their first chosen fake"). The text lives in a sibling
 * field whose name follows an irregular template per category. This helper
 * resolves the suffix to the actual text. Returns null if the field is not
 * a known selected_* field, the suffix is empty, or the referenced text
 * field is empty.
 *
 * Used by display sites that show selected_* values to a human reviewer.
 * Same conceptual mapping lives worker-side as CARD_EXPANSIONS in
 * routes/airtable.js for GAMES CORE transfer.
 */
const SELECTED_TEXT_PREFIX: Record<string, string> = {
  selected_wrong_1: 'wrong_def_',
  selected_wrong_2: 'wrong_def_',
  selected_wrong_3: 'wrong_def_',
  selected_example_1: 'example_sentence_',
  selected_example_2: 'example_sentence_',
  selected_example_3: 'example_sentence_',
  selected_how_to_use_1: 'how_to_use_',
  selected_how_to_use_2: 'how_to_use_',
  selected_how_to_use_3: 'how_to_use_',
  selected_fun_fact_1: 'fun_fact_',
  selected_fun_fact_2: 'fun_fact_',
  selected_fun_fact_3: 'fun_fact_',
  selected_newsletter_reframe: 'newsletter_reframe_',
  selected_second_chance_reframe: 'second_chance_reframe_',
  selected_third_chance_reframe: 'third_chance_reframe_',
};

export function resolveSelectedText(fieldName: string, word: Word): string | null {
  const suffix = String(word[fieldName] ?? '').trim();
  if (!suffix) return null;
  const textPrefix = SELECTED_TEXT_PREFIX[fieldName];
  if (!textPrefix) return null;
  const text = word[`${textPrefix}${suffix}`];
  return typeof text === 'string' && text.trim() ? text : null;
}
